import React, { FormEvent, useEffect, useState } from 'react'
import { navigate } from 'gatsby'
import { Helmet } from 'react-helmet'
import { Button } from '@/components/atoms/Button/Button'
import { renderFormField } from '@/helpers/formFieldRenderer'
import { hubspotFormSubmission } from '@/helpers/hubspotFormSubmission'
import { removeFoldersFromUrl } from '@/helpers/removeFoldersFromUrl'
import { HubspotFormProps, FormData } from './types'

const SITE_KEY = process.env.GATSBY_CLOUDFLARE_CAPTCHA_SITE_KEY

export function HubspotForm({ fields, button, hubspot_form_id, hubspot_portal_id, redirect_url }: HubspotFormProps) {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    surname: '',
    email: '',
    company: '',
    role: null,
    gdpr: false
  })
  const [successMessage, setSuccessMessage] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [captchaToken, setCaptchaToken] = useState<string | null>(null)

  const formRedirect = redirect_url?.url || removeFoldersFromUrl(redirect_url?.cached_url)

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (window.turnstile && document.getElementById('cf-turnstile')) {
        clearInterval(intervalId)
        window.turnstile.render('#cf-turnstile', {
          sitekey: SITE_KEY,
          callback: (token: string) => {
            setCaptchaToken(token)
            setErrorMessage('') // Clear the error message once CAPTCHA is validated
          }
        })
      }
    }, 100)

    return () => clearInterval(intervalId)
  }, [])

  const onChangeHandler = (name: string, value: unknown): void => {
    setFormData({ ...formData, [name]: value })
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()

    if (!captchaToken) {
      setErrorMessage('CAPTCHA validation failed. Please try again.')
      return
    }

    const legalConsentOptions = fields.some((field) => field.name === 'gdpr') && {
      consent: {
        consentToProcess: String(formData.gdpr),
        text: 'I have read and understood the Privacy Policy'
      }
    }
    hubspotFormSubmission(
      hubspot_portal_id,
      hubspot_form_id,
      [
        {
          name: 'firstname',
          value: formData.name
        },
        {
          name: 'lastname',
          value: formData.surname
        },
        {
          name: 'email',
          value: formData.email
        },
        {
          name: 'company',
          value: formData.company
        },
        {
          name: 'jobTitle',
          value: formData.role?.value as string
        }
      ],
      legalConsentOptions,
      setSuccessMessage,
      setErrorMessage
    )
    if (redirect_url && !errorMessage) {
      navigate(formRedirect)
    }
  }

  return (
    <>
      <Helmet>
        <script src="https://challenges.cloudflare.com/turnstile/v0/api.js" async defer />
      </Helmet>
      <form className="w-full bg-white shadow-form rounded p-24" onSubmit={handleSubmit}>
        <div className="flex flex-col tablet:flex-row tablet:gap-16 netbook:flex-col netbook:gap-0 desktop:flex-row desktop:gap-16">
          {fields?.slice(0, 2).map((field) => (
            <div className="w-full">
              <p className="text-bodyL font-bold text-text-dark-900 mb-8">{field.label}</p>
              {renderFormField({
                component: field.component,
                field,
                // @ts-ignore
                value: formData[field.name],
                onChange: onChangeHandler
              })}
            </div>
          ))}
        </div>
        {fields?.slice(2, 3).map((field) => (
          <>
            <p className="text-bodyL font-bold text-text-dark-900 mb-8">{field.label}</p>
            {renderFormField({
              component: field.component,
              field,
              // @ts-ignore
              value: formData[field.name],
              onChange: onChangeHandler
            })}
          </>
        ))}
        <div className="flex flex-col tablet:flex-row tablet:gap-4 netbook:flex-col netbook:gap-0 desktop:flex-row desktop:gap-4">
          {fields?.slice(3, 5).map((field) => (
            <div className="w-full">
              <p className="text-bodyL font-bold text-text-dark-900 mb-8">{field.label}</p>
              {renderFormField({
                component: field.component,
                field,
                // @ts-ignore
                value: formData[field.name],
                onChange: onChangeHandler
              })}
            </div>
          ))}
        </div>
        <div className="flex flex-col items-center mb-32">
          {fields?.slice(5).map((field) => (
            <div className="w-full">
              <p className="text-bodyL font-bold text-text-dark-900 mb-8">{field.label}</p>
              {renderFormField({
                component: field.component,
                field,
                // @ts-ignore
                value: formData[field.name],
                onChange: onChangeHandler
              })}
            </div>
          ))}
        </div>
        <div className="flex w-full justify-center items-center">
          {button.length > 0 ? (
            <Button {...button[0]} variant="primary-large" className="w-full mobile:w-fit" type="submit">
              {button[0].text}
            </Button>
          ) : null}
        </div>

        {successMessage || errorMessage ? (
          <div
            dangerouslySetInnerHTML={{ __html: successMessage || errorMessage }}
            className="text-sm text-center mt-20"
          />
        ) : null}
        <div id="cf-turnstile" className="mt-20 flex justify-center" />
      </form>
    </>
  )
}
